/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import { deepMerge } from './utilities';

export const META_CONFIG = 'fxa-config';

export interface Config {
  env: string;
  l10n: {
    strict: boolean;
    baseUrl: string;
  };
  marketingEmailPreferencesUrl: string;
  mfa: {
    otp: {
      expiresInMinutes: number;
    };
  };
  metrics: {
    navTiming: {
      enabled: boolean;
      endpoint: string;
    };
  };
  sentry: {
    dsn: string;
    env: string;
    sampleRate: number;
    tracesSampleRate: number;
    serverName: string;
    clientName: string;
    version: string;
  };
  servers: {
    auth: {
      url: string;
    };
    oauth: {
      url: string;
    };
    profile: {
      url: string;
    };
    paymentsNext: {
      url: string;
    };
  };
  oauth: {
    clientId: string;
    scopedKeysEnabled: boolean;
    scopedKeysValidation: Record<string, any>;
    isPromptNoneEnabled: boolean;
    isPromptNoneEnabledClientIds: string[];
    reactClientIdsEnabled: string[];
    clientInfoTimeout: number;
    clientInfoRetries: number;
  };
  recoveryCodes: {
    count: number;
    length: number;
  };
  passkeys: {
    maxPerUser: number;
  };
  version: string;
  googleAuthConfig: {
    enabled: boolean;
    clientId: string;
    redirectUri: string;
    authorizationEndpoint: string;
  };
  appleAuthConfig: {
    enabled: boolean;
    clientId: string;
    redirectUri: string;
    authorizationEndpoint: string;
  };
  brandMessagingMode?: string;
  glean: {
    enabled: boolean;
    applicationId: string;
    uploadEnabled: boolean;
    appDisplayVersion: string;
    appChannel: string;
    serverEndpoint: string;
    logPings: boolean;
    debugViewTag: string;
  };
  redirectAllowlist: string[];
  showReactApp: {
    signUpRoutes: boolean;
    signInRoutes: boolean;
    emailFirstRoutes: boolean;
    pairRoutes: boolean;
  };
  pairing: {
    browserBuild: 'firefox' | 'fenix';
    /** iOS URL scheme the pairing hand-off opens. See `pairing.ios_url_scheme`. */
    iosUrlScheme: string;
    /**
     * Whether a pairing QR scanned outside Firefox on iOS is handed off to the
     * Firefox app. See `pairing.ios_handoff`.
     */
    iosHandoff: boolean;
    clients: string[];
    serverBaseUri: string;
    version: number;
  };
  mobileStoreLinks: {
    ios: string;
    android: string;
  };
  rolloutRates?: {
    keyStretchV2?: number;
    generalizedReactApp?: number;
  };
  featureFlags?: {
    keyStretchV2?: boolean;
    recoveryCodeSetupOnSyncSignIn?: boolean;
    showLocaleToggle?: boolean;
    paymentsNextSubscriptionManagement?: boolean;
    passkeysEnabled?: boolean;
    passkeyRegistrationEnabled?: boolean;
    passkeyAuthenticationEnabled?: boolean;
    passkeyPasswordlessSyncEnabled?: boolean;
    passwordlessEnabled?: boolean;
  };
  darkMode?: {
    enabled?: boolean;
  };
  nimbus: {
    enabled: boolean;
    preview: boolean;
  };
  cms: {
    enabled: boolean;
    l10nEnabled: boolean;
  };
  servicesWithEmailVerification: string[];
}

export function getDefault() {
  return {
    env: 'development',
    l10n: {
      strict: false,
      baseUrl: '/settings/static',
    },
    marketingEmailPreferencesUrl: 'https://basket.mozilla.org/fxa/',
    metrics: {
      navTiming: { enabled: false, endpoint: '/check-your-metrics-config' },
    },
    mfa: {
      otp: {
        expiresInMinutes: 5,
      },
    },
    sentry: {
      dsn: '',
      env: 'local',
      serverName: 'fxa-settings-server',
      clientName: 'fxa-settings-client',
      sampleRate: 1.0,
      tracesSampleRate: 0,
      version: '',
    },
    servers: {
      auth: {
        url: '',
      },
      oauth: {
        url: '',
      },
      profile: {
        url: '',
      },
      paymentsNext: {
        url: '',
      },
    },
    oauth: {
      clientId: '',
      scopedKeysEnabled: true,
      scopedKeysValidation: {},
      isPromptNoneEnabled: false,
      isPromptNoneEnabledClientIds: new Array<string>(),
      reactClientIdsEnabled: new Array<string>(),
      clientInfoRetries: 4,
      clientInfoTimeout: 10_000,
    },
    recoveryCodes: {
      count: 8,
      length: 10,
    },
    passkeys: {
      maxPerUser: 10,
    },
    version: '',
    googleAuthConfig: {
      enabled: false,
      clientId: '',
      redirectUri: '',
      authorizationEndpoint: '',
    },
    appleAuthConfig: {
      enabled: false,
      clientId: '',
      redirectUri: '',
      authorizationEndpoint: '',
    },
    brandMessagingMode: 'prelaunch',
    glean: {
      enabled: false,
      applicationId: 'accounts_frontend_dev',
      uploadEnabled: true,
      appDisplayVersion: '',
      appChannel: 'development',
      serverEndpoint: 'https://incoming.telemetry.mozilla.org',
      logPings: false,
      debugViewTag: '',
    },
    redirectAllowlist: ['localhost'],
    showReactApp: {
      signUpRoutes: false,
      signInRoutes: false,
      emailFirstRoutes: false,
      pairRoutes: false,
    },
    pairing: {
      browserBuild: 'firefox',
      iosUrlScheme: 'firefox',
      iosHandoff: false,
      clients: [],
      serverBaseUri: 'wss://channelserver.services.mozilla.com',
      version: 1,
    },
    mobileStoreLinks: {
      ios: 'https://apps.apple.com/app/firefox-private-safe-browser/id989804926',
      android:
        'https://play.google.com/store/apps/details?id=org.mozilla.firefox',
    },
    featureFlags: {
      recoveryCodeSetupOnSyncSignIn: false,
      showLocaleToggle: false,
      paymentsNextSubscriptionManagement: false,
      passwordlessEnabled: false,
    },
    darkMode: {
      enabled: false,
    },
    cms: {
      enabled: false,
      l10nEnabled: false,
    },
    nimbus: {
      enabled: true,
      preview: true,
    },
    servicesWithEmailVerification: ['32aaeb6f1c21316a'],
  } as Config;
}

export function readConfigMeta(
  headQuerySelector: typeof document.head.querySelector
) {
  const metaEl = headQuerySelector(`meta[name="${META_CONFIG}"]`);

  if (!metaEl) {
    throw new Error('<meta name="fxa-config"> is missing');
  }

  const metaConfig = decode(metaEl.getAttribute('content'));

  return update(metaConfig);
}

export function decode(content: string | null) {
  const isDev = process.env.NODE_ENV === 'development';

  if (!content) {
    if (isDev) {
      console.warn('fxa-settings is missing server config');
    } else {
      throw new Error('Configuration is empty');
    }
  }

  const decoded = decodeURIComponent(content!);

  try {
    return JSON.parse(decoded);
  } catch (error) {
    if (isDev) {
      console.warn('fxa-settings server config is invalid');
    } else {
      throw new Error(
        `Invalid configuration ${JSON.stringify(content)}: ${decoded}`
      );
    }
  }
}

export function reset() {
  const initial = getDefault();

  // Reset existing default keys back to their original values
  const initialRecord = initial as unknown as Record<string, unknown>;
  const configRecord = config as unknown as Record<string, unknown>;
  for (const key of Object.keys(initialRecord)) {
    configRecord[key] = initialRecord[key];
  }

  // Remove any foreign keys that may have found their way in
  for (const key of Object.keys(configRecord)) {
    if (!(key in initialRecord)) {
      delete configRecord[key];
    }
  }
}

export function update(newData: { [key: string]: any }) {
  return deepMerge(config, newData);
}

const config: Config = getDefault();
export default config;
